import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Tab,
  Tabs,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Save,
  Add,
  Edit,
  Delete,
  ArrowBack,
  Computer,
  PhoneAndroid,
  DirectionsBike,
  AdminPanelSettings,
  CheckCircle,
  Circle,
  Refresh,
  Sync,
  Warning,
  Error,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { firebaseAdminService } from '../../services/firebaseAdminService';

interface ThemeConfig {
  id?: number;
  name: string;
  app_type: 'admin' | 'pos' | 'customer' | 'rider';
  is_active: boolean;
  _firebaseId?: string; // Firebase document ID for updates/deletes
  primary_color: string;
  secondary_color: string;
  text_color: string;
  background_color: string;
  accent_color: string;
  success_color: string;
  warning_color: string;
  error_color: string;
  info_color: string;
  border_color: string;
  card_color: string;
  header_color: string;
  footer_color: string;
  logo_url?: string;
  font_family: string;
  // Button styling
  button_background_color?: string;
  button_text_color?: string;
  button_hover_color?: string;
  navigation_sidebar_active_color?: string;
  navigation_sidebar_inactive_color?: string;
  navigation_sidebar_text_color?: string;
  navigation_sidebar_hover_color?: string;
  category_button_color?: string;
  // KPI Cards styling
  kpi_card_background?: string;
  kpi_card_text_color?: string;
  kpi_icon_color_1?: string;
  kpi_icon_color_2?: string;
  kpi_icon_color_3?: string;
  kpi_icon_color_4?: string;
  // Status indicators
  status_success_color?: string;
  status_pending_color?: string;
  status_error_color?: string;
  status_info_color?: string;
  // Charts & Data Visualization
  chart_primary_color?: string;
  chart_secondary_color?: string;
  chart_tertiary_color?: string;
  chart_quaternary_color?: string;
  // Mobile Customer App - Navigation & Buttons
  customer_top_nav_background?: string;
  customer_top_nav_text?: string;
  customer_bottom_nav_background?: string;
  customer_bottom_nav_active?: string;
  customer_bottom_nav_inactive?: string;
  customer_add_to_cart_bg?: string;
  customer_add_to_cart_text?: string;
  customer_browse_button_bg?: string;
  // Mobile Customer App - Menu & Product Cards
  customer_category_active?: string;
  customer_category_active_text?: string;
  customer_category_inactive?: string;
  customer_category_inactive_text?: string;
  customer_menu_card_bg?: string;
  customer_product_name?: string;
  customer_product_price?: string;
  customer_product_description?: string;
  // Mobile Customer App - Special Elements
  customer_rating_star?: string;
  customer_rating_text?: string;
  customer_price_tag_bg?: string;
  customer_price_tag_text?: string;
  customer_location_pin?: string;
  customer_notification_bell?: string;
  customer_profile_avatar_bg?: string;
  customer_profile_avatar_icon?: string;
  // Mobile Rider App - Navigation & Header
  rider_top_nav_background?: string;
  rider_top_nav_text?: string;
  rider_bottom_nav_background?: string;
  rider_bottom_nav_active?: string;
  rider_bottom_nav_inactive?: string;
  // Mobile Rider App - Buttons & Actions
  rider_start_delivery_bg?: string;
  rider_start_delivery_text?: string;
  rider_action_button_bg?: string;
  rider_action_button_text?: string;
  // Mobile Rider App - KPI Cards & Stats
  rider_kpi_card_bg?: string;
  rider_kpi_card_text?: string;
  rider_kpi_icon_orders?: string;
  rider_kpi_icon_earnings?: string;
  rider_kpi_icon_distance?: string;
  rider_kpi_icon_rating?: string;
  // Mobile Rider App - Order Cards & Status
  rider_order_card_bg?: string;
  rider_order_card_text?: string;
  rider_status_confirmed?: string;
  rider_status_pending?: string;
  rider_status_delivered?: string;
  rider_status_ready?: string;
  // Mobile Rider App - Profile & Stats
  rider_profile_avatar_bg?: string;
  rider_profile_avatar_text?: string;
  rider_profile_status_active?: string;
  rider_profile_status_text?: string;
  rider_quick_stats_bg?: string;
  rider_quick_stats_text?: string;
  version?: number;
  created_at?: string;
  updated_at?: string;
}

const ThemePage: React.FC = () => {
  const { muiTheme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [themes, setThemes] = useState<ThemeConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTheme, setEditingTheme] = useState<ThemeConfig | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [activatingThemeId, setActivatingThemeId] = useState<number | null>(null);
  const [themeStatus, setThemeStatus] = useState<{[key: number]: 'active' | 'inactive' | 'testing' | 'error'}>({});
  const [formData, setFormData] = useState<ThemeConfig>({
    name: '',
    app_type: 'admin',
    is_active: false,
    primary_color: '#28a745',
    secondary_color: '#f8f9fa',
    text_color: '#000000',
    background_color: '#ffffff',
    accent_color: '#17a2b8',
    success_color: '#28a745',
    warning_color: '#ffc107',
    error_color: '#dc3545',
    info_color: '#17a2b8',
    border_color: '#dee2e6',
    card_color: '#ffffff',
    header_color: '#343a40',
    footer_color: '#f8f9fa',
    logo_url: '',
    font_family: 'Poppins',
    // Button styling
    button_background_color: '#1976D2',
    button_text_color: '#000000',
    button_hover_color: '#1565C0',
    category_button_color: '#FF9800',
    // KPI Cards styling
    kpi_card_background: '#ffffff',
    kpi_card_text_color: '#000000',
    kpi_icon_color_1: '#1976D2',
    kpi_icon_color_2: '#4CAF50',
    kpi_icon_color_3: '#FF9800',
    kpi_icon_color_4: '#9C27B0',
    // Status indicators
    status_success_color: '#4CAF50',
    status_pending_color: '#FF9800',
    status_error_color: '#F44336',
    status_info_color: '#2196F3',
    // Charts & Data Visualization
    chart_primary_color: '#4CAF50',
    chart_secondary_color: '#FF6B6B',
    chart_tertiary_color: '#1976D2',
    chart_quaternary_color: '#FF9800',
    // Mobile Customer App - Navigation & Buttons
    customer_top_nav_background: '#000000',
    customer_top_nav_text: '#FFFFFF',
    customer_bottom_nav_background: '#FFFFFF',
    customer_bottom_nav_active: '#4CAF50',
    customer_bottom_nav_inactive: '#666666',
    customer_add_to_cart_bg: '#000000',
    customer_add_to_cart_text: '#FFFFFF',
    customer_browse_button_bg: '#000000',
    // Mobile Customer App - Menu & Product Cards
    customer_category_active: '#000000',
    customer_category_active_text: '#FFFFFF',
    customer_category_inactive: '#FFFFFF',
    customer_category_inactive_text: '#000000',
    customer_menu_card_bg: '#FFFFFF',
    customer_product_name: '#000000',
    customer_product_price: '#000000',
    customer_product_description: '#666666',
    // Mobile Customer App - Special Elements
    customer_rating_star: '#FFD700',
    customer_rating_text: '#4CAF50',
    customer_price_tag_bg: '#E8F5E8',
    customer_price_tag_text: '#4CAF50',
    customer_location_pin: '#4CAF50',
    customer_notification_bell: '#4CAF50',
    customer_profile_avatar_bg: '#000000',
    customer_profile_avatar_icon: '#FFFFFF',
    // Mobile Rider App - Navigation & Header
    rider_top_nav_background: '#FF6B6B',
    rider_top_nav_text: '#FFFFFF',
    rider_bottom_nav_background: '#FFFFFF',
    rider_bottom_nav_active: '#FF6B6B',
    rider_bottom_nav_inactive: '#757575',
    // Mobile Rider App - Buttons & Actions
    rider_start_delivery_bg: '#FF6B6B',
    rider_start_delivery_text: '#FFFFFF',
    rider_action_button_bg: '#FF6B6B',
    rider_action_button_text: '#FFFFFF',
    // Mobile Rider App - KPI Cards & Stats
    rider_kpi_card_bg: '#FFFFFF',
    rider_kpi_card_text: '#000000',
    rider_kpi_icon_orders: '#000000',
    rider_kpi_icon_earnings: '#4CAF50',
    rider_kpi_icon_distance: '#2196F3',
    rider_kpi_icon_rating: '#FF6B6B',
    // Mobile Rider App - Order Cards & Status
    rider_order_card_bg: '#FFFFFF',
    rider_order_card_text: '#000000',
    rider_status_confirmed: '#757575',
    rider_status_pending: '#FF9800',
    rider_status_delivered: '#4CAF50',
    rider_status_ready: '#2196F3',
    // Mobile Rider App - Profile & Stats
    rider_profile_avatar_bg: '#E0E0E0',
    rider_profile_avatar_text: '#000000',
    rider_profile_status_active: '#4CAF50',
    rider_profile_status_text: '#FFFFFF',
    rider_quick_stats_bg: '#FFFFFF',
    rider_quick_stats_text: '#000000',
  });

  const appIcons = {
    admin: <AdminPanelSettings />,
    pos: <Computer />,
    customer: <PhoneAndroid />,
    rider: <DirectionsBike />,
  };

  const appColors = {
    admin: '#1976D2',
    pos: '#E91E63',
    customer: '#4CAF50',
    rider: '#FF5722',
  };

  const appNames = {
    admin: t('theme.adminTheme'),
    pos: t('theme.posTheme'),
    customer: t('theme.customerTheme'),
    rider: t('theme.riderTheme'),
  };

  const fontOptions = [
    'Poppins',
    'Roboto',
    'Inter',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Nunito',
    'Source Sans Pro',
  ];

  useEffect(() => {
    loadThemes();
    
    // Set up periodic status checking every 30 seconds
    const statusCheckInterval = setInterval(async () => {
      try {
        const response = await fetch('https://__SAHEEH_LEGACY_BACKEND_UNSET__/api/v1/theme/public/?app=pos');
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && data.theme) {
            // Update theme status based on current active theme
            setThemeStatus(prev => ({
              ...prev,
              [data.theme.id]: 'active'
            }));
          }
        }
      } catch (error) {
        console.warn('Periodic status check failed:', error);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(statusCheckInterval);
  }, []);

  const loadThemes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load themes from Firebase
      const firebaseThemes = await firebaseAdminService.getThemes();
      console.log('🎨 [THEME] Loaded themes from Firebase:', firebaseThemes);
      
      // Convert Firebase themes to ThemeConfig format
      // Generate a stable numeric ID from Firebase document ID
      const convertedThemes: ThemeConfig[] = firebaseThemes.map((theme: any, index: number) => {
        // Generate a stable numeric ID from Firebase document ID
        let numericId = 0;
        if (theme.id) {
          // Simple hash function to convert string ID to number
          for (let i = 0; i < theme.id.length; i++) {
            numericId = ((numericId << 5) - numericId) + theme.id.charCodeAt(i);
            numericId = numericId & numericId; // Convert to 32-bit integer
          }
          numericId = Math.abs(numericId) || (index + 1);
      } else {
          numericId = index + 1;
        }
        
        // Clean font_family - remove ", sans-serif" or similar fallbacks
        let fontFamily = theme.font_family || 'Poppins';
        if (typeof fontFamily === 'string' && fontFamily.includes(',')) {
          fontFamily = fontFamily.split(',')[0].trim();
        }
        
        return {
        id: numericId,
        name: theme.name || 'Unnamed Theme',
        app_type: theme.app_type || 'admin',
        is_active: theme.is_active || false,
        primary_color: theme.primary_color || '#28a745',
        secondary_color: theme.secondary_color || '#f8f9fa',
        text_color: theme.text_color || '#000000',
        background_color: theme.background_color || '#ffffff',
        accent_color: theme.accent_color || '#17a2b8',
        success_color: theme.success_color || '#28a745',
        warning_color: theme.warning_color || '#ffc107',
        error_color: theme.error_color || '#dc3545',
        info_color: theme.info_color || '#17a2b8',
        border_color: theme.border_color || '#dee2e6',
        card_color: theme.card_color || '#ffffff',
        header_color: theme.header_color || '#343a40',
        footer_color: theme.footer_color || '#f8f9fa',
        logo_url: theme.logo_url || '',
        font_family: fontFamily,
        // Button styling
        button_background_color: theme.button_background_color,
        button_text_color: theme.button_text_color,
        button_hover_color: theme.button_hover_color,
        navigation_sidebar_active_color: theme.navigation_sidebar_active_color,
        navigation_sidebar_inactive_color: theme.navigation_sidebar_inactive_color,
        navigation_sidebar_text_color: theme.navigation_sidebar_text_color,
        navigation_sidebar_hover_color: theme.navigation_sidebar_hover_color,
        category_button_color: theme.category_button_color,
        // KPI Cards styling
        kpi_card_background: theme.kpi_card_background,
        kpi_card_text_color: theme.kpi_card_text_color,
        kpi_icon_color_1: theme.kpi_icon_color_1,
        kpi_icon_color_2: theme.kpi_icon_color_2,
        kpi_icon_color_3: theme.kpi_icon_color_3,
        kpi_icon_color_4: theme.kpi_icon_color_4,
        // Status indicators
        status_success_color: theme.status_success_color,
        status_pending_color: theme.status_pending_color,
        status_error_color: theme.status_error_color,
        status_info_color: theme.status_info_color,
        // Charts & Data Visualization
        chart_primary_color: theme.chart_primary_color,
        chart_secondary_color: theme.chart_secondary_color,
        chart_tertiary_color: theme.chart_tertiary_color,
        chart_quaternary_color: theme.chart_quaternary_color,
        // Mobile app specific colors
        customer_top_nav_background: theme.customer_top_nav_background,
        customer_top_nav_text: theme.customer_top_nav_text,
        customer_bottom_nav_background: theme.customer_bottom_nav_background,
        customer_bottom_nav_active: theme.customer_bottom_nav_active,
        customer_bottom_nav_inactive: theme.customer_bottom_nav_inactive,
        customer_add_to_cart_bg: theme.customer_add_to_cart_bg,
        customer_add_to_cart_text: theme.customer_add_to_cart_text,
        customer_browse_button_bg: theme.customer_browse_button_bg,
        customer_category_active: theme.customer_category_active,
        customer_category_active_text: theme.customer_category_active_text,
        customer_category_inactive: theme.customer_category_inactive,
        customer_category_inactive_text: theme.customer_category_inactive_text,
        customer_menu_card_bg: theme.customer_menu_card_bg,
        customer_product_name: theme.customer_product_name,
        customer_product_price: theme.customer_product_price,
        customer_product_description: theme.customer_product_description,
        customer_rating_star: theme.customer_rating_star,
        customer_rating_text: theme.customer_rating_text,
        customer_price_tag_bg: theme.customer_price_tag_bg,
        customer_price_tag_text: theme.customer_price_tag_text,
        customer_location_pin: theme.customer_location_pin,
        customer_notification_bell: theme.customer_notification_bell,
        customer_profile_avatar_bg: theme.customer_profile_avatar_bg,
        customer_profile_avatar_icon: theme.customer_profile_avatar_icon,
        rider_top_nav_background: theme.rider_top_nav_background,
        rider_top_nav_text: theme.rider_top_nav_text,
        rider_bottom_nav_background: theme.rider_bottom_nav_background,
        rider_bottom_nav_active: theme.rider_bottom_nav_active,
        rider_bottom_nav_inactive: theme.rider_bottom_nav_inactive,
        rider_start_delivery_bg: theme.rider_start_delivery_bg,
        rider_start_delivery_text: theme.rider_start_delivery_text,
        rider_action_button_bg: theme.rider_action_button_bg,
        rider_action_button_text: theme.rider_action_button_text,
        rider_kpi_card_bg: theme.rider_kpi_card_bg,
        rider_kpi_card_text: theme.rider_kpi_card_text,
        rider_kpi_icon_orders: theme.rider_kpi_icon_orders,
        rider_kpi_icon_earnings: theme.rider_kpi_icon_earnings,
        rider_kpi_icon_distance: theme.rider_kpi_icon_distance,
        rider_kpi_icon_rating: theme.rider_kpi_icon_rating,
        rider_order_card_bg: theme.rider_order_card_bg,
        rider_order_card_text: theme.rider_order_card_text,
        rider_status_confirmed: theme.rider_status_confirmed,
        rider_status_pending: theme.rider_status_pending,
        rider_status_delivered: theme.rider_status_delivered,
        rider_status_ready: theme.rider_status_ready,
        rider_profile_avatar_bg: theme.rider_profile_avatar_bg,
        rider_profile_avatar_text: theme.rider_profile_avatar_text,
        rider_profile_status_active: theme.rider_profile_status_active,
        rider_profile_status_text: theme.rider_profile_status_text,
        rider_quick_stats_bg: theme.rider_quick_stats_bg,
        rider_quick_stats_text: theme.rider_quick_stats_text,
        created_at: theme.created_at || new Date().toISOString(),
        updated_at: theme.updated_at || new Date().toISOString(),
        _firebaseId: theme.id, // Store Firebase document ID for updates/deletes
        };
      });
      
      setThemes(convertedThemes);
      console.log('✅ [THEME] Loaded', convertedThemes.length, 'themes from Firebase');
    } catch (error) {
      console.error('❌ [THEME] Failed to load themes:', error);
      setError('Failed to load themes from Firebase. Please try again.');
      setThemes([]);
    } finally {
      setLoading(false);
    }
  };

  const getThemesByApp = (appType: string) => {
    return themes.filter(theme => theme.app_type === appType);
  };

  const getActiveTheme = (appType: string) => {
    return themes.find(theme => theme.app_type === appType && theme.is_active);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleOpenDialog = (theme?: ThemeConfig) => {
    if (theme) {
      setEditingTheme(theme);
      setFormData(theme);
    } else {
      setEditingTheme(null);
      setFormData({
        name: '',
        app_type: 'admin',
        is_active: false,
        primary_color: '#28a745',
        secondary_color: '#f8f9fa',
        text_color: '#000000',
        background_color: '#ffffff',
        accent_color: '#17a2b8',
        success_color: '#28a745',
        warning_color: '#ffc107',
        error_color: '#dc3545',
        info_color: '#17a2b8',
        border_color: '#dee2e6',
        card_color: '#ffffff',
        header_color: '#343a40',
        footer_color: '#f8f9fa',
        logo_url: '',
        font_family: 'Poppins',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTheme(null);
    setFormData({
      name: '',
      app_type: 'admin',
      is_active: false,
      primary_color: '#28a745',
      secondary_color: '#f8f9fa',
      text_color: '#000000',
      background_color: '#ffffff',
      accent_color: '#17a2b8',
      success_color: '#28a745',
      warning_color: '#ffc107',
      error_color: '#dc3545',
      info_color: '#17a2b8',
      border_color: '#dee2e6',
      card_color: '#ffffff',
      header_color: '#343a40',
      footer_color: '#f8f9fa',
      logo_url: '',
      font_family: 'Poppins',
      // Button styling
      button_background_color: '#1976D2',
      button_text_color: '#000000',
      button_hover_color: '#1565C0',
      category_button_color: '#FF9800',
      // KPI Cards styling
      kpi_card_background: '#ffffff',
      kpi_card_text_color: '#000000',
      kpi_icon_color_1: '#1976D2',
      kpi_icon_color_2: '#4CAF50',
      kpi_icon_color_3: '#FF9800',
      kpi_icon_color_4: '#9C27B0',
      // Status indicators
      status_success_color: '#4CAF50',
      status_pending_color: '#FF9800',
      status_error_color: '#F44336',
      status_info_color: '#2196F3',
      // Charts & Data Visualization
      chart_primary_color: '#4CAF50',
      chart_secondary_color: '#FF6B6B',
      chart_tertiary_color: '#1976D2',
      chart_quaternary_color: '#FF9800',
      // Mobile Customer App - Navigation & Buttons
      customer_top_nav_background: '#000000',
      customer_top_nav_text: '#FFFFFF',
      customer_bottom_nav_background: '#FFFFFF',
      customer_bottom_nav_active: '#4CAF50',
      customer_bottom_nav_inactive: '#666666',
      customer_add_to_cart_bg: '#000000',
      customer_add_to_cart_text: '#FFFFFF',
      customer_browse_button_bg: '#000000',
      // Mobile Customer App - Menu & Product Cards
      customer_category_active: '#000000',
      customer_category_active_text: '#FFFFFF',
      customer_category_inactive: '#FFFFFF',
      customer_category_inactive_text: '#000000',
      customer_menu_card_bg: '#FFFFFF',
      customer_product_name: '#000000',
      customer_product_price: '#000000',
      customer_product_description: '#666666',
      // Mobile Customer App - Special Elements
      customer_rating_star: '#FFD700',
      customer_rating_text: '#4CAF50',
      customer_price_tag_bg: '#E8F5E8',
      customer_price_tag_text: '#4CAF50',
      customer_location_pin: '#4CAF50',
      customer_notification_bell: '#4CAF50',
      customer_profile_avatar_bg: '#000000',
      customer_profile_avatar_icon: '#FFFFFF',
      // Mobile Rider App - Navigation & Header
      rider_top_nav_background: '#FF6B6B',
      rider_top_nav_text: '#FFFFFF',
      rider_bottom_nav_background: '#FFFFFF',
      rider_bottom_nav_active: '#FF6B6B',
      rider_bottom_nav_inactive: '#757575',
      // Mobile Rider App - Buttons & Actions
      rider_start_delivery_bg: '#FF6B6B',
      rider_start_delivery_text: '#FFFFFF',
      rider_action_button_bg: '#FF6B6B',
      rider_action_button_text: '#FFFFFF',
      // Mobile Rider App - KPI Cards & Stats
      rider_kpi_card_bg: '#FFFFFF',
      rider_kpi_card_text: '#000000',
      rider_kpi_icon_orders: '#000000',
      rider_kpi_icon_earnings: '#4CAF50',
      rider_kpi_icon_distance: '#2196F3',
      rider_kpi_icon_rating: '#FF6B6B',
      // Mobile Rider App - Order Cards & Status
      rider_order_card_bg: '#FFFFFF',
      rider_order_card_text: '#000000',
      rider_status_confirmed: '#757575',
      rider_status_pending: '#FF9800',
      rider_status_delivered: '#4CAF50',
      rider_status_ready: '#2196F3',
      // Mobile Rider App - Profile & Stats
      rider_profile_avatar_bg: '#E0E0E0',
      rider_profile_avatar_text: '#000000',
      rider_profile_status_active: '#4CAF50',
      rider_profile_status_text: '#FFFFFF',
      rider_quick_stats_bg: '#FFFFFF',
      rider_quick_stats_text: '#000000',
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare theme data for Firebase (remove _firebaseId and id from the data)
        const { _firebaseId: _, id: __, ...themeData } = formData as any;
        const firebaseThemeData = {
          ...themeData,
          app_type: formData.app_type || 'admin',
          // Ensure card_color matches background_color for unified background
          card_color: formData.background_color,
        };
      
      // Use editingTheme._firebaseId if available, otherwise try formData._firebaseId
      const firebaseId = editingTheme?._firebaseId || (formData as any)._firebaseId;
      
      if (editingTheme && firebaseId) {
        // Update existing theme - ensure firebaseId is a string
        const themeIdString = String(firebaseId);
        console.log('🔄 [THEME] Updating theme with ID:', themeIdString, 'Type:', typeof firebaseId);
        await firebaseAdminService.updateTheme(themeIdString, firebaseThemeData);
        setSuccess('Theme updated successfully!');
      } else {
        // Create new theme
        console.log('➕ [THEME] Creating new theme');
        await firebaseAdminService.createTheme(firebaseThemeData);
        setSuccess('Theme created successfully!');
      }
      
        await loadThemes();
        handleCloseDialog();
    } catch (error: any) {
      console.error('❌ [THEME] Error saving theme:', error);
      setError(error?.message || 'Failed to save theme. Please check your permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (themeId: number) => {
    try {
      setActivatingThemeId(themeId);
      setThemeStatus(prev => ({ ...prev, [themeId]: 'testing' }));
      setError(null);
      
      console.log('🎨 [THEME] Activating theme ID:', themeId);
      
      // Find the theme to get its Firebase ID and app_type
      const theme = themes.find(t => t.id === themeId);
      if (!theme) {
        setError('Theme not found');
        setActivatingThemeId(null);
        return;
      }
      
      const firebaseId = (theme as any)._firebaseId;
      if (!firebaseId) {
        setError('Theme Firebase ID not found');
        setActivatingThemeId(null);
        return;
      }
      
      // Activate theme in Firebase
      await firebaseAdminService.setActiveTheme(firebaseId, theme.app_type);
      
      console.log('✅ [THEME] Theme activated in Firebase');
      
      // Reload themes to get updated status
      await loadThemes();
      
          setThemeStatus(prev => ({ ...prev, [themeId]: 'active' }));
      setSuccess(`✅ Theme activated successfully! Changes are now live for ${theme.app_type.toUpperCase()} app.`);
    } catch (error: any) {
      console.error('❌ [THEME] Error activating theme:', error);
      setThemeStatus(prev => ({ ...prev, [themeId]: 'error' }));
      setError(`Failed to activate theme: ${error?.message || 'Unknown error'}`);
    } finally {
      setActivatingThemeId(null);
    }
  };

  const testThemeApplication = async (themeId: number) => {
    try {
      console.log('🧪 Testing theme application to POS app...');
      
      // Force cache busting with timestamp parameter
      const timestamp = Date.now();
      const testResponse = await fetch(`https://__SAHEEH_LEGACY_BACKEND_UNSET__/api/v1/theme/public/?app=pos&_t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
      });
      
      if (testResponse.ok) {
        const testData = await testResponse.json();
        console.log('✅ POS theme test response:', testData);
        
        if (testData.status === 'success' && testData.theme && testData.theme.id === themeId) {
          console.log('🎉 Theme successfully applied to POS app!');
          
          // Send cache bust signal to POS app
          await sendCacheBustSignal();
          
          return true;
        } else {
          console.warn('⚠️ Theme not yet applied to POS app');
          return false;
        }
      } else {
        console.error('❌ POS theme test failed:', testResponse.status);
        return false;
      }
    } catch (error: any) {
      console.error('❌ Theme application test failed:', error);
      return false;
    }
  };

  const sendCacheBustSignal = async () => {
    try {
      console.log('🚀 Sending cache bust signal to POS app...');
      
      // Send a signal to clear POS app cache
      const cacheBustResponse = await fetch('https://__SAHEEH_LEGACY_BACKEND_UNSET__/api/v1/theme/cache-bust/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          app: 'pos',
          timestamp: Date.now(),
          action: 'clear_cache'
        })
      });
      
      if (cacheBustResponse.ok) {
        console.log('✅ Cache bust signal sent successfully');
      } else {
        console.warn('⚠️ Cache bust signal failed, but theme may still work');
      }
    } catch (error) {
      console.warn('⚠️ Cache bust signal failed:', error);
    }
  };

  const handleDeleteTheme = async (theme: ThemeConfig) => {
    const firebaseId = theme._firebaseId;
    if (!firebaseId) {
      setError('Cannot delete theme: Firebase ID not found');
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the theme "${theme.name}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await firebaseAdminService.deleteTheme(firebaseId);
      
        setSuccess(`Theme "${theme.name}" deleted successfully!`);
        await loadThemes();
    } catch (error: any) {
      console.error('❌ [THEME] Error deleting theme:', error);
      setError(error?.message || 'Failed to delete theme. Please check your permissions.');
    } finally {
      setLoading(false);
    }
  };

  const ColorPreviewCard = ({ theme, appType }: { theme: ThemeConfig; appType: string }) => (
    <Card 
        sx={{
        height: 200,
        background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.accent_color} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              {theme.name}
      </Typography>
            <Chip 
              icon={appIcons[theme.app_type as keyof typeof appIcons]}
              label={appNames[theme.app_type as keyof typeof appNames]}
              size="small"
              sx={{ 
                mt: 1,
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
              }}
            />
    </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {theme.is_active ? (
              <Badge badgeContent={<CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />}>
                <Circle sx={{ fontSize: 24, color: 'rgba(255,255,255,0.8)' }} />
              </Badge>
            ) : (
              <Circle sx={{ fontSize: 24, color: 'rgba(255,255,255,0.5)' }} />
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Box 
            sx={{
              width: 20, 
              height: 20, 
              backgroundColor: theme.primary_color,
              borderRadius: '50%',
              border: '2px solid white',
            }} 
          />
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              backgroundColor: theme.secondary_color,
              borderRadius: '50%',
              border: '2px solid white',
            }} 
          />
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              backgroundColor: theme.accent_color,
              borderRadius: '50%',
              border: '2px solid white',
            }} 
          />
        </Box>
      </CardContent>
    </Card>
  );

  const AppSection = ({ appType }: { appType: 'admin' | 'pos' | 'customer' | 'rider' }) => {
    const appThemes = getThemesByApp(appType);
    const activeTheme = getActiveTheme(appType);

    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ backgroundColor: appColors[appType], mr: 2 }}>
            {appIcons[appType]}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {appNames[appType]}
            </Typography>
            <Box component="div" sx={{ color: '#000000', fontSize: '0.875rem' }}>
              {activeTheme ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    backgroundColor: '#4CAF50',
                    animation: 'pulse 2s infinite'
                  }} />
                  <span>{t('theme.active')}: {activeTheme.name}</span>
                  {themeStatus[activeTheme.id!] === 'active' && (
                    <Chip 
                      label="Live" 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#4CAF50', 
                        color: 'white',
                        fontSize: '0.7rem',
                        height: 20
                      }} 
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    backgroundColor: '#FF9800'
                  }} />
                  <span>{t('theme.noActiveTheme')}</span>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
              onClick={() => {
                setFormData(prev => ({ ...prev, app_type: appType }));
                handleOpenDialog();
              }}
            sx={{
                backgroundColor: appColors[appType],
                '&:hover': { backgroundColor: appColors[appType], opacity: 0.9 },
              }}
            >
              {t('theme.newTheme')}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
          {appThemes.map((theme) => (
            <Grid item xs={12} sm={6} md={4} key={theme.id}>
              <Box sx={{ position: 'relative' }}>
                <ColorPreviewCard theme={theme} appType={appType} />
                
                <Box sx={{ 
                    position: 'absolute',
                    top: 16,
                    right: 16,
                  display: 'flex',
                  gap: 1,
                }}>
                    <Tooltip title={t('common.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(theme)}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                      }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton
                          size="small"
                        onClick={() => handleDeleteTheme(theme)}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        '&:hover': { backgroundColor: 'rgba(255,0,0,0.3)' }
                      }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  
                  <Tooltip title={
                    activatingThemeId === theme.id ? t('theme.testingTheme') :
                    theme.is_active ? t('theme.activeThemeLive') :
                    t('theme.clickToActivate')
                  }>
                    <IconButton
                      size="small"
                      onClick={() => theme.id && handleActivate(theme.id)}
                      disabled={activatingThemeId === theme.id}
                      sx={{ 
                        backgroundColor: theme.is_active ? 'rgba(76,175,80,0.9)' : 
                                        themeStatus[theme.id!] === 'error' ? 'rgba(244,67,54,0.8)' :
                                        'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        opacity: activatingThemeId === theme.id ? 0.7 : 1,
                        '&:hover': { 
                          backgroundColor: theme.is_active ? 'rgba(76,175,80,1)' : 
                                          themeStatus[theme.id!] === 'error' ? 'rgba(244,67,54,1)' :
                                          'rgba(255,255,255,0.3)' 
                        },
                        '&:disabled': {
                          opacity: 0.7,
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      {activatingThemeId === theme.id ? (
                        <Sync fontSize="small" sx={{ animation: 'spin 1s linear infinite' }} />
                      ) : theme.is_active ? (
                        <CheckCircle fontSize="small" />
                      ) : themeStatus[theme.id!] === 'error' ? (
                        <Error fontSize="small" />
                      ) : (
                        <Circle fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  </Box>
                </Box>
            </Grid>
          ))}
          
          {appThemes.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>
                  {t('theme.noThemesCreated')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000000', mb: 2 }}>
                  {t('theme.createFirstTheme', { app: appNames[appType] })}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, app_type: appType }));
                    handleOpenDialog();
                  }}
                >
                  {t('theme.createTheme')}
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
                </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>{t('theme.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      maxWidth: 1200, 
      mx: 'auto',
      '& *': { color: '#000000 !important' },
      '& .MuiTypography-root': { color: '#000000 !important' },
      '& .MuiInputLabel-root': { color: '#000000 !important' },
      '& .MuiOutlinedInput-root': { color: '#000000 !important' },
      '& .MuiInputBase-input': { color: '#000000 !important' },
      '& .MuiSelect-select': { color: '#000000 !important' },
      '& .MuiMenuItem-root': { color: '#000000 !important' },
      '& .MuiFormControlLabel-label': { color: '#000000 !important' },
      '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
      },
      '@keyframes pulse': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.5 },
        '100%': { opacity: 1 }
      }
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            {t('theme.title')}
                  </Typography>
          <Typography variant="body1" sx={{ color: '#000000' }}>
            {t('theme.subtitle')}
                  </Typography>
                </Box>
      </Box>

      {/* Live Status Indicator */}
      <Paper sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              🎨 Live Theme Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                backgroundColor: '#4CAF50',
                animation: 'pulse 2s infinite'
              }} />
              <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 500 }}>
                {t('theme.connectedToPOS')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={() => {
              loadThemes();
              setSuccess('Theme status refreshed');
            }}
            sx={{ 
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': { backgroundColor: 'rgba(76,175,80,0.1)' }
            }}
          >
            {t('theme.refreshStatus')}
          </Button>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* App Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
          <Tab 
            icon={<AdminPanelSettings />} 
            label={t('theme.adminTheme')}
            iconPosition="start"
          />
          <Tab
            icon={<Computer />}
            label={t('theme.posTheme')}
            iconPosition="start"
          />
          <Tab
            icon={<PhoneAndroid />}
            label={t('theme.customerTheme')}
            iconPosition="start"
          />
          <Tab
            icon={<DirectionsBike />}
            label={t('theme.riderTheme')}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* App Sections */}
      {currentTab === 0 && <AppSection appType="admin" />}
      {currentTab === 1 && <AppSection appType="pos" />}
      {currentTab === 2 && <AppSection appType="customer" />}
      {currentTab === 3 && <AppSection appType="rider" />}

      {/* Edit/Create Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: appColors[formData.app_type] }}>
              {appIcons[formData.app_type]}
            </Avatar>
            <Box>
              <Typography variant="h6">
          {editingTheme ? t('theme.editTheme') : t('theme.createNewTheme')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                {appNames[formData.app_type]}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('theme.themeName')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ 
                  mb: 2,
                  '& .MuiInputLabel-root': { color: '#000000' },
                  '& .MuiOutlinedInput-root': { color: '#000000' },
                  '& .MuiInputBase-input': { color: '#000000' }
                }}
              />
              
              <FormControl fullWidth sx={{ 
                mb: 2,
                '& .MuiInputLabel-root': { color: '#000000' },
                '& .MuiOutlinedInput-root': { color: '#000000' },
                '& .MuiSelect-select': { color: '#000000' }
              }}>
                <InputLabel sx={{ color: '#000000' }}>{t('theme.appType')}</InputLabel>
                <Select
                  value={formData.app_type}
                  onChange={(e) => setFormData({ ...formData, app_type: e.target.value as any })}
                  label={t('theme.appType')}
                  sx={{ color: '#000000' }}
                >
                  <MenuItem value="admin">{t('theme.adminTheme')}</MenuItem>
                  <MenuItem value="pos">{t('theme.posTheme')}</MenuItem>
                  <MenuItem value="customer">{t('theme.customerTheme')}</MenuItem>
                  <MenuItem value="rider">{t('theme.riderTheme')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>{t('theme.coreColors')}</Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('theme.primaryColor')}
                type="color"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    sx={{ mb: 2, '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
              />
            </Grid>
                <Grid item xs={6}>
              <TextField
                fullWidth
                    label={t('theme.backgroundColor')}
                type="color"
                    value={formData.background_color}
                    onChange={(e) => {
                      const bgColor = e.target.value;
                      // Set both background_color and card_color to the same value
                      setFormData({ ...formData, background_color: bgColor, card_color: bgColor });
                    }}
                    sx={{ mb: 2, '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Applies to main page background, cards, sidebar, and all containers"
              />
            </Grid>
                <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('theme.textColor')}
                type="color"
                value={formData.text_color}
                onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                    sx={{ mb: 2, '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
              />
            </Grid>
                <Grid item xs={6}>
              <TextField
                fullWidth
                    label={t('theme.accentColor')}
                type="color"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    sx={{ mb: 2, '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Additional Colors - Available for POS and Admin */}
            {(formData.app_type === 'pos' || formData.app_type === 'admin') && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>{t('theme.additionalColors')}</Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Secondary"
                type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
              />
            </Grid>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Success"
                type="color"
                value={formData.success_color}
                onChange={(e) => setFormData({ ...formData, success_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
              />
            </Grid>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Warning"
                type="color"
                value={formData.warning_color}
                onChange={(e) => setFormData({ ...formData, warning_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
              />
            </Grid>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Error"
                type="color"
                value={formData.error_color}
                onChange={(e) => setFormData({ ...formData, error_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
              />
            </Grid>
              </Grid>
            </Grid>
            )}

            {/* POS App Specific Sections */}
            {formData.app_type === 'pos' && (
              <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🔘 All Buttons (Dashboard/Orders/Payments/Reports Pages)</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls all buttons across the entire POS application including action buttons, navigation buttons, and form buttons.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Button Background Color"
                type="color"
                    value={formData.button_background_color || '#1976D2'}
                    onChange={(e) => setFormData({ ...formData, button_background_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Changes: Refresh, Export, Filter, Add to Cart, Checkout buttons"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Button Text Color"
                    type="color"
                    value={formData.button_text_color || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, button_text_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Text color on all buttons"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Button Hover Color"
                    type="color"
                    value={formData.button_hover_color || '#1565C0'}
                    onChange={(e) => setFormData({ ...formData, button_hover_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Color when hovering over buttons"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Navigation Sidebar Active Color"
                    type="color"
                    value={formData.navigation_sidebar_active_color || '#FF8E53'}
                    onChange={(e) => setFormData({ ...formData, navigation_sidebar_active_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Left sidebar active menu item (Dashboard, Orders, etc.)"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🧭 Navigation Sidebar (Left Menu)</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the left navigation sidebar colors for Dashboard, Orders, Payments, Reports, and Logout buttons.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Active Button Background"
                    type="color"
                    value={formData.navigation_sidebar_active_color || '#FF8E53'}
                    onChange={(e) => setFormData({ ...formData, navigation_sidebar_active_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Background color for active page (Dashboard, Orders, etc.)"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Inactive Button Background"
                    type="color"
                    value={formData.navigation_sidebar_inactive_color || '#f8f9fa'}
                    onChange={(e) => setFormData({ ...formData, navigation_sidebar_inactive_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Background color for inactive pages"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Button Text Color"
                    type="color"
                    value={formData.navigation_sidebar_text_color || '#495057'}
                    onChange={(e) => setFormData({ ...formData, navigation_sidebar_text_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Text color for all navigation buttons"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Button Hover Color"
                    type="color"
                    value={formData.navigation_sidebar_hover_color || '#e9ecef'}
                    onChange={(e) => setFormData({ ...formData, navigation_sidebar_hover_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Background color when hovering over buttons"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>📊 Dashboard Categories & Filters</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the category filter buttons on the Dashboard page (All, Crispy Roll, Nigiri, etc.)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={6}>
              <TextField
                fullWidth
                    label="Category Filter Button Color"
                type="color"
                    value={formData.category_button_color || '#FF9800'}
                    onChange={(e) => setFormData({ ...formData, category_button_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Dashboard category buttons (All, Sushi Rolls, Nigiri, etc.)"
                  />
                </Grid>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Category Button Text Color"
                    type="color"
                    value={formData.text_color || '#000000'}
                    onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Text color on category filter buttons"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>📈 KPI Cards (All Pages: Dashboard/Orders/Payments/Reports)</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the summary cards showing key metrics like Total Revenue, Total Orders, etc. across all pages.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="KPI Card Background"
                type="color"
                    value={formData.kpi_card_background || '#ffffff'}
                    onChange={(e) => setFormData({ ...formData, kpi_card_background: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Card background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Card Text Color"
                    type="color"
                    value={formData.kpi_card_text_color || '#000000'}
                    onChange={(e) => setFormData({ ...formData, kpi_card_text_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Text color on KPI cards"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Icon Color 1 (Blue)"
                    type="color"
                    value={formData.kpi_icon_color_1 || '#1976D2'}
                    onChange={(e) => setFormData({ ...formData, kpi_icon_color_1: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Total Revenue icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Icon Color 2 (Green)"
                    type="color"
                    value={formData.kpi_icon_color_2 || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, kpi_icon_color_2: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Total Orders icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Icon Color 3 (Orange)"
                    type="color"
                    value={formData.kpi_icon_color_3 || '#FF9800'}
                    onChange={(e) => setFormData({ ...formData, kpi_icon_color_3: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Total Customers icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Icon Color 4 (Purple)"
                    type="color"
                    value={formData.kpi_icon_color_4 || '#9C27B0'}
                    onChange={(e) => setFormData({ ...formData, kpi_icon_color_4: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Avg Order Value icon color"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🏷️ Status Indicators (Orders/Payments)</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the status badges showing order and payment statuses (confirmed, pending, delivered, failed, etc.)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Success Status Color"
                type="color"
                    value={formData.status_success_color || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, status_success_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Confirmed, Delivered, Completed"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Pending Status Color"
                    type="color"
                    value={formData.status_pending_color || '#FF9800'}
                    onChange={(e) => setFormData({ ...formData, status_pending_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Pending, Preparing, Ready"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Error Status Color"
                    type="color"
                    value={formData.status_error_color || '#F44336'}
                    onChange={(e) => setFormData({ ...formData, status_error_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Failed, Cancelled, Error"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Info Status Color"
                    type="color"
                    value={formData.status_info_color || '#2196F3'}
                    onChange={(e) => setFormData({ ...formData, status_info_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="On Delivery, Processing"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>📊 Charts & Graphs (Reports Page)</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls all chart colors with gradient support for Revenue Trend, Payment Methods, Sales by Hour, etc.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                    label="Chart Primary Color (Green)"
                type="color"
                    value={formData.chart_primary_color || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, chart_primary_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Revenue Trend chart, PayPal segments"
              />
            </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Chart Secondary Color (Red)"
                    type="color"
                    value={formData.chart_secondary_color || '#FF6B6B'}
                    onChange={(e) => setFormData({ ...formData, chart_secondary_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Sales by Hour bars, Error indicators"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Chart Tertiary Color (Blue)"
                    type="color"
                    value={formData.chart_tertiary_color || '#1976D2'}
                    onChange={(e) => setFormData({ ...formData, chart_tertiary_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Card payment segments, Info indicators"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Chart Quaternary Color (Orange)"
                    type="color"
                    value={formData.chart_quaternary_color || '#FF9800'}
                    onChange={(e) => setFormData({ ...formData, chart_quaternary_color: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Cash segments, Warning indicators"
                  />
                </Grid>
              </Grid>
            </Grid>
              </>
            )}

            {/* Customer App Specific Sections */}
            {formData.app_type === 'customer' && (
              <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>📱 Mobile Customer App - Navigation & Buttons</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the mobile customer app navigation bars, buttons, and interactive elements.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Top Navigation Background"
                    type="color"
                    value={formData.customer_top_nav_background || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_top_nav_background: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Top bar background (Home, Menu, Cart, Orders, Profile pages)"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Top Navigation Text"
                    type="color"
                    value={formData.customer_top_nav_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_top_nav_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Top bar text and icons color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bottom Navigation Background"
                    type="color"
                    value={formData.customer_bottom_nav_background || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_bottom_nav_background: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Bottom navigation bar background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bottom Navigation Active"
                    type="color"
                    value={formData.customer_bottom_nav_active || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, customer_bottom_nav_active: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Active tab color (Home, Menu, Cart, Orders, Profile)"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bottom Navigation Inactive"
                    type="color"
                    value={formData.customer_bottom_nav_inactive || '#666666'}
                    onChange={(e) => setFormData({ ...formData, customer_bottom_nav_inactive: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Inactive tab color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Add to Cart Button Background"
                    type="color"
                    value={formData.customer_add_to_cart_bg || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_add_to_cart_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Add to Cart button background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Add to Cart Button Text"
                    type="color"
                    value={formData.customer_add_to_cart_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_add_to_cart_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Add to Cart button text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Browse Menu Button Background"
                    type="color"
                    value={formData.customer_browse_button_bg || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_browse_button_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Browse Menu button background"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🍱 Mobile Customer App - Menu & Product Cards</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls menu item cards, category buttons, and product display elements.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Category Button Active"
                    type="color"
                    value={formData.customer_category_active || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_category_active: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Active category button (All, Sushi Rolls, etc.)"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Category Button Active Text"
                    type="color"
                    value={formData.customer_category_active_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_category_active_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Active category button text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Category Button Inactive"
                    type="color"
                    value={formData.customer_category_inactive || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_category_inactive: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Inactive category button background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Category Button Inactive Text"
                    type="color"
                    value={formData.customer_category_inactive_text || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_category_inactive_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Inactive category button text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Menu Card Background"
                    type="color"
                    value={formData.customer_menu_card_bg || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_menu_card_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Menu item card background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Product Name Color"
                    type="color"
                    value={formData.customer_product_name || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_product_name: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Product name text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Product Price Color"
                    type="color"
                    value={formData.customer_product_price || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_product_price: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Product price text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Product Description Color"
                    type="color"
                    value={formData.customer_product_description || '#666666'}
                    onChange={(e) => setFormData({ ...formData, customer_product_description: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Product description text color"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>⭐ Mobile Customer App - Special Elements</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls special elements like ratings, price tags, icons, and profile elements.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Rating Star Color"
                    type="color"
                    value={formData.customer_rating_star || '#FFD700'}
                    onChange={(e) => setFormData({ ...formData, customer_rating_star: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Star rating color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Rating Text Color"
                    type="color"
                    value={formData.customer_rating_text || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, customer_rating_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Rating number text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Price Tag Background"
                    type="color"
                    value={formData.customer_price_tag_bg || '#E8F5E8'}
                    onChange={(e) => setFormData({ ...formData, customer_price_tag_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Price tag background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Price Tag Text"
                    type="color"
                    value={formData.customer_price_tag_text || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, customer_price_tag_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Price tag text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Location Pin Color"
                    type="color"
                    value={formData.customer_location_pin || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, customer_location_pin: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Location pin icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Notification Bell Color"
                    type="color"
                    value={formData.customer_notification_bell || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, customer_notification_bell: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Notification bell icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Profile Avatar Background"
                    type="color"
                    value={formData.customer_profile_avatar_bg || '#000000'}
                    onChange={(e) => setFormData({ ...formData, customer_profile_avatar_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Profile avatar background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Profile Avatar Icon"
                    type="color"
                    value={formData.customer_profile_avatar_icon || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, customer_profile_avatar_icon: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Profile avatar icon color"
                  />
                </Grid>
              </Grid>
            </Grid>
              </>
            )}

            {/* Rider App Specific Sections */}
            {formData.app_type === 'rider' && (
              <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🏍️ Mobile Rider App - Navigation & Header</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the mobile rider app navigation bars, header, and top-level navigation elements.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Top Navigation Background"
                    type="color"
                    value={formData.rider_top_nav_background || '#FF6B6B'}
                    onChange={(e) => setFormData({ ...formData, rider_top_nav_background: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Top header background (Dashboard, Orders, Earnings, Profile pages)"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Top Navigation Text"
                    type="color"
                    value={formData.rider_top_nav_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_top_nav_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Top header text and icons color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bottom Navigation Background"
                    type="color"
                    value={formData.rider_bottom_nav_background || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_bottom_nav_background: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Bottom navigation bar background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bottom Navigation Active"
                    type="color"
                    value={formData.rider_bottom_nav_active || '#FF6B6B'}
                    onChange={(e) => setFormData({ ...formData, rider_bottom_nav_active: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Active tab color (Dashboard, Orders, Earnings, Profile)"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bottom Navigation Inactive"
                    type="color"
                    value={formData.rider_bottom_nav_inactive || '#757575'}
                    onChange={(e) => setFormData({ ...formData, rider_bottom_nav_inactive: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Inactive tab color"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🚀 Mobile Rider App - Buttons & Actions</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls action buttons, delivery buttons, and interactive elements in the rider app.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Delivery Button Background"
                    type="color"
                    value={formData.rider_start_delivery_bg || '#FF6B6B'}
                    onChange={(e) => setFormData({ ...formData, rider_start_delivery_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Start Delivery button background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Delivery Button Text"
                    type="color"
                    value={formData.rider_start_delivery_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_start_delivery_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Start Delivery button text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Action Buttons Background"
                    type="color"
                    value={formData.rider_action_button_bg || '#FF6B6B'}
                    onChange={(e) => setFormData({ ...formData, rider_action_button_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="General action buttons background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Action Buttons Text"
                    type="color"
                    value={formData.rider_action_button_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_action_button_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="General action buttons text color"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>📊 Mobile Rider App - KPI Cards & Stats</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls the KPI cards showing orders, earnings, distance, and rating statistics.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Cards Background"
                    type="color"
                    value={formData.rider_kpi_card_bg || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_kpi_card_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="KPI cards background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="KPI Cards Text"
                    type="color"
                    value={formData.rider_kpi_card_text || '#000000'}
                    onChange={(e) => setFormData({ ...formData, rider_kpi_card_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="KPI cards text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Orders KPI Icon"
                    type="color"
                    value={formData.rider_kpi_icon_orders || '#000000'}
                    onChange={(e) => setFormData({ ...formData, rider_kpi_icon_orders: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Orders KPI icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Earnings KPI Icon"
                    type="color"
                    value={formData.rider_kpi_icon_earnings || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, rider_kpi_icon_earnings: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Earnings KPI icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Distance KPI Icon"
                    type="color"
                    value={formData.rider_kpi_icon_distance || '#2196F3'}
                    onChange={(e) => setFormData({ ...formData, rider_kpi_icon_distance: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Distance KPI icon color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Rating KPI Icon"
                    type="color"
                    value={formData.rider_kpi_icon_rating || '#FF6B6B'}
                    onChange={(e) => setFormData({ ...formData, rider_kpi_icon_rating: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Rating KPI icon color"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>📦 Mobile Rider App - Order Cards & Status</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls order cards, status indicators, and order-related UI elements.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Order Cards Background"
                    type="color"
                    value={formData.rider_order_card_bg || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_order_card_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Order cards background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Order Cards Text"
                    type="color"
                    value={formData.rider_order_card_text || '#000000'}
                    onChange={(e) => setFormData({ ...formData, rider_order_card_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Order cards text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Confirmed Status"
                    type="color"
                    value={formData.rider_status_confirmed || '#757575'}
                    onChange={(e) => setFormData({ ...formData, rider_status_confirmed: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Confirmed order status color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Pending Status"
                    type="color"
                    value={formData.rider_status_pending || '#FF9800'}
                    onChange={(e) => setFormData({ ...formData, rider_status_pending: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Pending order status color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Delivered Status"
                    type="color"
                    value={formData.rider_status_delivered || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, rider_status_delivered: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Delivered order status color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Ready Status"
                    type="color"
                    value={formData.rider_status_ready || '#2196F3'}
                    onChange={(e) => setFormData({ ...formData, rider_status_ready: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Ready order status color"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>👤 Mobile Rider App - Profile & Stats</Typography>
              <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                Controls profile elements, avatar, status indicators, and quick stats cards.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Profile Avatar Background"
                    type="color"
                    value={formData.rider_profile_avatar_bg || '#E0E0E0'}
                    onChange={(e) => setFormData({ ...formData, rider_profile_avatar_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Profile avatar background color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Profile Avatar Text"
                    type="color"
                    value={formData.rider_profile_avatar_text || '#000000'}
                    onChange={(e) => setFormData({ ...formData, rider_profile_avatar_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Profile avatar text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Active Status Color"
                    type="color"
                    value={formData.rider_profile_status_active || '#4CAF50'}
                    onChange={(e) => setFormData({ ...formData, rider_profile_status_active: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Active status indicator color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Status Text Color"
                    type="color"
                    value={formData.rider_profile_status_text || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_profile_status_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Status text color"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Quick Stats Background"
                    type="color"
                    value={formData.rider_quick_stats_bg || '#FFFFFF'}
                    onChange={(e) => setFormData({ ...formData, rider_quick_stats_bg: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Quick stats cards background"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Quick Stats Text"
                    type="color"
                    value={formData.rider_quick_stats_text || '#000000'}
                    onChange={(e) => setFormData({ ...formData, rider_quick_stats_text: e.target.value })}
                    size="small"
                    sx={{ '& .MuiInputLabel-root': { color: '#000000' }, '& .MuiOutlinedInput-root': { color: '#000000' } }}
                    helperText="Quick stats text color"
                  />
                </Grid>
              </Grid>
            </Grid>
              </>
            )}

            {/* Admin App Specific Sections */}
            {formData.app_type === 'admin' && (
              <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ color: '#000000' }} gutterBottom>🎛️ Admin Panel Specific</Typography>
                <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                  Controls admin panel specific elements like dashboard cards, tables, and management interfaces.
                </Typography>
                <Typography variant="body2" sx={{ color: '#999999', mb: 2 }}>
                  Admin panel uses the standard color scheme. Additional customization options will be added in future updates.
                </Typography>
              </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth sx={{
                '& .MuiInputLabel-root': { color: '#000000' },
                '& .MuiOutlinedInput-root': { color: '#000000' },
                '& .MuiSelect-select': { color: '#000000' }
              }}>
                <InputLabel sx={{ color: '#000000' }}>{t('theme.fontFamily')}</InputLabel>
                <Select
                  value={formData.font_family || 'Poppins'}
                  onChange={(e) => setFormData({ ...formData, font_family: String(e.target.value).trim() })}
                  label={t('theme.fontFamily')}
                  sx={{ color: '#000000' }}
                >
                  {fontOptions.map((font) => (
                    <MenuItem key={font} value={font} sx={{ fontFamily: font, color: '#000000' }}>
                      {font}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                label={t('theme.activateTheme')}
                sx={{ '& .MuiFormControlLabel-label': { color: '#000000' } }}
                />
              </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<Save />}
            disabled={loading}
          >
            {editingTheme ? t('theme.updateTheme') : t('theme.createTheme')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThemePage;